import { useEffect, useRef, useState } from "react";
import { createConsumer } from "@rails/actioncable";

type JobState = "pending" | "running" | "ready" | "failed";

interface AiJobStatusMessage {
  type:   "state_changed";
  id:     number;
  state:  JobState;
  result: Record<string, unknown>;
  error:  string | null;
}

interface Options {
  onReady?:    (result: Record<string, unknown>) => void;
  onFailed?:   (error: string | null) => void;
  deadlineMs?: number;
}

const DEFAULT_DEADLINE_MS = 90_000;

// Subscribes to AiJobStatusChannel for one async AI run (task 2dc337d2) and
// drives idle -> generating -> ready | failed(retry). SolidQueue persistence
// (Slice 0) means a queued job survives a Fly machine restart, so the
// deadline here is a secondary UX guard against a hung job or a cold-started
// worker — not the durability mechanism.
export function useAiJobStatus(jobId: number | null, { onReady, onFailed, deadlineMs = DEFAULT_DEADLINE_MS }: Options = {}) {
  const [state, setState]     = useState<JobState | "idle">(jobId ? "pending" : "idle");
  const [timedOut, setTimedOut] = useState(false);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!jobId) {
      setState("idle");
      setTimedOut(false);
      return;
    }

    setState("pending");
    setTimedOut(false);

    function armDeadline() {
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
      deadlineRef.current = setTimeout(() => setTimedOut(true), deadlineMs);
    }

    function disarmDeadline() {
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
      deadlineRef.current = null;
    }

    const consumer = createConsumer();
    const sub = consumer.subscriptions.create({ channel: "AiJobStatusChannel", id: jobId }, {
      received(data: AiJobStatusMessage) {
        if (data.id !== jobId) return;
        setState(data.state);

        // Re-arm on running instead of clearing outright — the slow part (the
        // actual claude -p call) starts AFTER running, so a worker that dies
        // or a machine that auto-stops mid-run needs its own fresh window,
        // not to inherit whatever's left of the pending-stage deadline.
        if (data.state === "running") {
          armDeadline();
        } else {
          disarmDeadline();
        }

        if (data.state === "ready") onReady?.(data.result);
        if (data.state === "failed") onFailed?.(data.error);
      },
    });

    armDeadline();

    return () => {
      sub.unsubscribe();
      consumer.disconnect();
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
    };
  }, [jobId]);

  const generating = jobId != null && (state === "pending" || state === "running") && !timedOut;

  return { state, generating, timedOut };
}
